<template>
  <BasicBlock :title="$t('basic_info.block.title')">
    <div
      :class="
        singleColumn
          ? 'f-grid f-grid-cols-1 f-gap-2 sm:f-gap-3'
          : 'f-grid f-grid-cols-1 sm:f-grid-cols-2 f-gap-2 sm:f-gap-3'
      "
    >
      <DefaultInput
        class="fapi-form-default-input-email"
        name="email"
        type="email"
        queryParam="email"
        autocomplete="email"
        :orderIntentData="formData.email"
        :label="$t('basic_info.email.label')"
        :showInput="true"
        :showInputInFullForm="true"
        :singleColumn="singleColumn"
        :isFullForm="fullForm"
        :rules="{
          required: true,
          email: true,
        }"
        @input="$emit('basicDataChanged', $event)"
      ></DefaultInput>

      <BasePhone
        class="fapi-form-default-input-phone"
        name="phone"
        type="tel"
        queryParam="mobil"
        autocomplete="tel"
        :rules="emptyRules"
        :formResource="formResource"
        :label="$t('basic_info.phone.label')"
        :orderIntentData="formData.phone"
        :showInput="fieldSettings.phone.show"
        :showInputInFullForm="false"
        :isFullForm="fullForm"
        :required="fieldSettings.phone.required"
        :singleColumn="singleColumn"
        :country="formData.address.country"
        @input="$emit('basicDataChanged', $event)"
      ></BasePhone>

      <ShowFullForm
        class="fapi-form-default-show-company"
        v-if="!isFullForm || showCompanyInput"
        @change="fullFormChanged"
        :forceFullInvoice="forceFullInvoice"
        :singleColumn="singleColumn"
      ></ShowFullForm>

      <DefaultInput
        class="fapi-form-default-input-company"
        name="company"
        type="text"
        queryParam="company"
        autocomplete="organization"
        :class="!singleColumn ? 'f-col-span-2' : ''"
        :label="$t('basic_info.company.label')"
        :showInput="showCompany"
        :showInputInFullForm="false"
        :isFullForm="fullForm"
        :rules="companyRules"
        :singleColumn="singleColumn"
        @input="$emit('basicDataChanged', $event)"
      />

      <DefaultInput
        class="fapi-form-default-input-ico"
        name="ico"
        type="text"
        queryParam="ic"
        autocomplete="on"
        :label="$t('basic_info.ic.label')"
        :showInput="showCompany"
        :showInputInFullForm="false"
        :isFullForm="fullForm"
        :rules="icoRules"
        :singleColumn="singleColumn"
        @input="$emit('basicDataChanged', $event)"
      ></DefaultInput>

      <DefaultInput
        class="fapi-form-default-input-dic"
        name="dic"
        type="text"
        queryParam="dic"
        autocomplete="on"
        :label="$t('basic_info.dic.label')"
        :showInput="showCompany"
        :showInputInFullForm="false"
        :isFullForm="fullForm"
        :rules="dicRules"
        :singleColumn="singleColumn"
        @input="$emit('basicDataChanged', $event)"
      ></DefaultInput>

      <DefaultInput
        class="fapi-form-default-input-icdph"
        name="ic_dph"
        type="text"
        queryParam="ic-dph"
        autocomplete="on"
        :label="$t('basic_info.ic_dph.label')"
        :showInput="showInputIcDph"
        :showInputInFullForm="false"
        :isFullForm="fullForm"
        :rules="icDphRules"
        :singleColumn="singleColumn"
        @input="$emit('basicDataChanged', $event)"
      ></DefaultInput>

      <DefaultInput
        class="fapi-form-default-input-first-name"
        name="first_name"
        type="text"
        queryParam="name"
        autocomplete="given-name"
        :label="$t('basic_info.first_name.label')"
        :orderIntentData="formData.first_name"
        :showInput="fieldSettings.name.show || showNeededShippingAddressByItem"
        :showInputInFullForm="true"
        :isFullForm="fullForm"
        :rules="nameRules"
        :singleColumn="singleColumn"
        @input="$emit('basicDataChanged', $event)"
      ></DefaultInput>

      <DefaultInput
        class="fapi-form-default-input-last-name"
        name="last_name"
        type="text"
        queryParam="surname"
        autocomplete="family-name"
        :label="$t('basic_info.last_name.label')"
        :orderIntentData="formData.last_name"
        :showInput="fieldSettings.name.show || showNeededShippingAddressByItem"
        :showInputInFullForm="true"
        :isFullForm="fullForm"
        :rules="nameRules"
        :singleColumn="singleColumn"
        @input="$emit('basicDataChanged', $event)"
      ></DefaultInput>

      <DefaultInput
        class="fapi-form-default-input-street"
        name="street"
        type="text"
        queryParam="street"
        autocomplete="street-address"
        :label="$t('basic_info.street.label')"
        :showInput="fieldSettings.address.show"
        :showInputInFullForm="true"
        :isFullForm="fullForm"
        :rules="streetRules"
        :singleColumn="singleColumn"
        @input="$emit('addressChanged', $event)"
      ></DefaultInput>

      <DefaultInput
        class="fapi-form-default-input-city"
        name="city"
        type="text"
        queryParam="city"
        autocomplete="address-level2"
        :label="$t('basic_info.city.label')"
        :showInput="fieldSettings.address.show"
        :showInputInFullForm="true"
        :isFullForm="fullForm"
        :rules="cityRules"
        :singleColumn="singleColumn"
        @input="$emit('addressChanged', $event)"
      ></DefaultInput>

      <DefaultInput
        class="fapi-form-default-input-zip"
        v-show="fieldSettings.address.show"
        name="zip"
        type="text"
        queryParam="postcode"
        autocomplete="postal-code"
        :label="$t('basic_info.zip.label')"
        :showInput="fieldSettings.address.show"
        :showInputInFullForm="true"
        :isFullForm="fullForm"
        :rules="zipRules"
        :singleColumn="singleColumn"
        @input="$emit('addressChanged', $event)"
      ></DefaultInput>

      <BaseCountry
        class="fapi-form-default-input-country"
        queryParam="state"
        autocomplete="country"
        name="country"
        :formResource="formResource"
        :showInput="
          fieldSettings.country.show || showNeededShippingAddressByItem
        "
        :showInputInFullForm="true"
        :isFullForm="fullForm"
        :required="fieldSettings.country.required"
        :singleColumn="singleColumn"
        @change="$emit('countryChanged', $event)"
      ></BaseCountry>

      <NotesInput
        class="fapi-form-default-input-notes"
        v-if="fieldSettings.note.show"
        name="notes"
        queryParam="notes"
        :class="{ 'f-col-span-2': !singleColumn }"
        :showInput="fieldSettings.note.show"
        :rules="noteRules"
        :label="fieldSettings.note.label"
        :singleColumn="singleColumn"
        @input="$emit('basicDataChanged', $event)"
      />

      <div
        v-if="
          (showShippingDetails &&
            formResource.field_settings?.address.show !== true) ||
          allowShippingAddress
        "
        :class="{
          'sm:f--ml-2 f-pl-2.5': singleColumn,
          'f-col-span-2': !singleColumn,
        }"
      >
        <BaseCheckbox
          class="fapi-form-default-input-show-shipping-address"
          name="show_shipping_address_input"
          :class="{ 'sm:f-ml-32': singleColumn }"
          :id="$id('show_shipping_address_input')"
          :disabled="forceShipping"
          @input="$emit('basicDataChanged', { with_shipping_address: $event })"
          v-model="withShippingAddress"
        >
          {{ $t("basic_info.show_shipping_address.label") }}
        </BaseCheckbox>
      </div>

      <template v-if="formData.with_shipping_address">
        <ShippingInput
          class="fapi-form-default-input-shipping-first-name"
          name="shipping_first_name"
          autocomplete="given-name"
          :label="$t('basic_info.shipping_first_name.label')"
          :show="fullForm"
          :rules="shippingNameRules"
          :singleColumn="singleColumn"
          @input="$emit('shippingAddressChanged', $event)"
        ></ShippingInput>
        <ShippingInput
          class="fapi-form-default-input-shipping-last-name"
          name="shipping_last_name"
          autocomplete="family-name"
          :show="fullForm"
          :label="$t('basic_info.shipping_last_name.label')"
          :rules="shippingNameRules"
          :singleColumn="singleColumn"
          @input="$emit('shippingAddressChanged', $event)"
        ></ShippingInput>
        <ShippingInput
          class="fapi-form-default-input-shipping-company"
          name="shipping_company"
          autocomplete="organization"
          :label="$t('basic_info.shipping_company.label')"
          :show="fullForm"
          :rules="shippingCompanyRules"
          :singleColumn="singleColumn"
          @input="$emit('shippingAddressChanged', $event)"
        ></ShippingInput>

        <ShippingInput
          class="fapi-form-default-input-shipping-street"
          name="shipping_street"
          :label="$t('basic_info.shipping_street.label')"
          :show="fullForm"
          :rules="shippingStreetRules"
          :singleColumn="singleColumn"
          @input="$emit('shippingAddressChanged', $event)"
        ></ShippingInput>
        <ShippingInput
          class="fapi-form-default-input-shipping-city"
          name="shipping_city"
          :label="$t('basic_info.shipping_city.label')"
          :show="fullForm"
          :rules="shippingCityRules"
          :singleColumn="singleColumn"
          @input="$emit('shippingAddressChanged', $event)"
        ></ShippingInput>
        <ShippingInput
          class="fapi-form-default-input-shipping-zip"
          name="shipping_zip"
          autocomplete="postal-code"
          :label="$t('basic_info.shipping_zip.label')"
          :show="fullForm"
          :rules="shippingZipRules"
          :singleColumn="singleColumn"
          @input="$emit('shippingAddressChanged', $event)"
        ></ShippingInput>
        <BaseCountry
          class="fapi-form-default-input-shipping-country"
          autocomplete="country"
          name="shipping_country"
          :formResource="formResource"
          :formData="formData"
          :showInput="formData.with_shipping_address"
          :showInputInFullForm="formData.with_shipping_address"
          :isFullForm="formData.with_shipping_address"
          :required="formData.with_shipping_address"
          :singleColumn="singleColumn"
          @change="$emit('shippingAddressChanged', { country: $event })"
        ></BaseCountry>
      </template>
    </div>
  </BasicBlock>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import BaseCountry from "@/components/Form/Basic/Inputs/BaseCountry.vue";
import ShippingInput from "@/components/Form/Basic/Inputs/ShippingInput.vue";
import FormResource from "@/module/Form/Resources/FormResource";
import BasicBlock from "@/components/Form/BasicBlock.vue";
import DefaultInput from "@/components/Form/Basic/Inputs/DefaultInput.vue";
import FormData from "@/module/FormData";
import TemplateSettings from "@/module/Form/Resources/TemplateSettings";
import FieldSettings from "@/module/Form/Resources/FieldSettings";
import { isStringOrNull } from "@/module/ComponentPropsValidations/validations";
import BaseCheckbox from "@/components/Form/Basic/Inputs/BaseCheckbox.vue";
import ShowFullForm from "@/components/Form/Basic/Inputs/ShowFullForm.vue";
import FormShippingItem from "@/module/FormShippingItem";
import retryableAsyncComponent from "@/module/Utils/RetryableAsyncComponent";

interface ComponentData {
  withShippingAddress: boolean;
  isCompany: boolean;
  fullForm: boolean;
}

export default Vue.extend({
  name: "BasicData",

  components: {
    BaseCheckbox,
    BasePhone: retryableAsyncComponent(
      () =>
        import(
          /* webpackChunkName: "phone-input" */ "@/components/Form/Basic/Inputs/BasePhone.vue"
        ),
    ),
    NotesInput: retryableAsyncComponent(
      () =>
        import(
          /* webpackChunkName: "notes" */ "@/components/Form/Basic/Inputs/NotesInput.vue"
        ),
    ),
    DefaultInput,
    BasicBlock,
    ShippingInput,
    BaseCountry,
    ShowFullForm,
  },

  props: {
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    templateSettings: {
      type: Object as PropType<TemplateSettings>,
      required: true,
    },
    country: {
      required: true,
      validator: isStringOrNull,
    },
    shippingCountry: {
      required: true,
      validator: isStringOrNull,
    },
    formData: {
      type: Object as PropType<FormData>,
      required: true,
    },
    forceFullInvoice: {
      type: Boolean,
      required: true,
    },
    fieldSettings: {
      type: Object as PropType<FieldSettings>,
      required: true,
    },
    showShippingDetails: {
      type: Boolean,
      required: true,
    },
    shippingMethods: {
      type: Array<FormShippingItem>,
      required: true,
    },
    showNeededShippingAddressByItem: {
      type: Boolean,
      required: true,
    },
    allowShippingAddress: {
      type: Boolean,
      required: true,
    },
  },

  data(): ComponentData {
    return {
      withShippingAddress: false,
      isCompany: false,
      fullForm: false,
    };
  },

  computed: {
    isFullForm(): boolean {
      return (
        (this.fieldSettings.name.required ||
          this.fieldSettings.company.required) &&
        this.fieldSettings.address.required
      );
    },

    isAddressShow(): boolean {
      return (
        this.fieldSettings.address.show || this.fieldSettings.address.required
      );
    },

    showCompany(): boolean {
      return (
        (this.fullForm && this.isCompany) ||
        this.isCompany ||
        this.fieldSettings.company.required
      );
    },

    showCompanyInput(): boolean {
      return (
        this.fullForm ||
        (this.fieldSettings.company.show &&
          !this.fieldSettings.company.required)
      );
    },

    forceShipping(): boolean {
      if (
        this.fieldSettings.address.show ||
        this.formData.full_invoice ||
        this.isFullForm
      ) {
        return false;
      }

      const selectedShippingMethod = this.shippingMethods.find(
        (shippingMethod) => {
          return (
            shippingMethod.shippingMethodResource.id ===
            this.formData.shipping_method_id
          );
        },
      );

      if (!selectedShippingMethod) {
        return false;
      }
      const { shippingMethodResource } = selectedShippingMethod;

      return (
        shippingMethodResource.type !== "personal_collection" &&
        shippingMethodResource.subtype !== "personal_collection" &&
        shippingMethodResource.subtype !== "packatery_personal_collection"
      );
    },

    singleColumn(): boolean {
      return (
        this.templateSettings.container_settings.billing.layout.column ===
        "single"
      );
    },

    showInputIcDph(): boolean {
      return this.country === "SK" && this.showCompany;
    },

    nameRules(): object {
      return {
        required:
          !this.isCompany &&
          (this.fieldSettings.name.required || this.fullForm) &&
          !this.fieldSettings.company.required,
      };
    },

    companyRules(): object {
      return {
        required: Boolean(
          this.fieldSettings.company.required ||
            this.isCompany ||
            (this.formData.ico && this.formData.ico.length > 0) ||
            (this.formData.company && this.formData.company.length > 0),
        ),
      };
    },

    icoRules(): object {
      return {
        required: Boolean(
          (this.fieldSettings.company.required ||
            this.isCompany ||
            (this.formData.ico && this.formData.ico.length > 0) ||
            (this.formData.company && this.formData.company.length > 0)) &&
            this.formData.address.country !== "GB",
        ),
        min: 5,
        max: 17,
        alpha_dash: true,
      };
    },

    dicRules(): object {
      const isSkOrXk = this.country === "SK" || this.country === "XK";

      return {
        required: false,
        numeric: isSkOrXk,
        alpha_num: true,
        min: 5,
        max: 20,
        vat_id: !isSkOrXk ? { country: this.country } : false,
      };
    },

    icDphRules(): object {
      return {
        required: false,
        alpha_num: true,
        min: 5,
        max: 20,
        vat_id: this.country === "SK" ? { country: this.country } : false,
      };
    },

    streetRules(): object {
      return {
        required: this.fieldSettings.address.required || this.fullForm,
        street_with_number: true,
      };
    },

    cityRules(): object {
      return {
        required: this.fieldSettings.address.required || this.fullForm,
      };
    },

    zipRules(): object {
      const validationCountry = this.country === "XK" ? "RS" : this.country;
      return {
        required: this.fieldSettings.address.required || this.fullForm,
        zip: { country: validationCountry },
      };
    },

    noteRules(): object {
      return { required: this.fieldSettings.note.required };
    },

    shippingNameRules(): object {
      return {
        required:
          this.formData.with_shipping_address &&
          this.formData.shipping_address.company === null,
      };
    },

    shippingCompanyRules(): object {
      return {
        required:
          this.formData.with_shipping_address &&
          this.formData.shipping_address.first_name === null &&
          this.formData.shipping_address.last_name === null,
      };
    },

    shippingStreetRules(): object {
      return {
        required: this.formData.with_shipping_address,
        street_with_number: this.formData.with_shipping_address,
      };
    },

    shippingCityRules(): object {
      return { required: this.formData.with_shipping_address };
    },

    shippingZipRules(): object {
      const validationShippingCountry =
        this.country === "XK" ? "RS" : this.country;
      return {
        required: this.formData.with_shipping_address,
        zip: { country: validationShippingCountry },
      };
    },

    emptyRules(): object {
      return {};
    },
  },

  methods: {
    fullFormChanged($event: boolean): void {
      this.fullForm = $event || this.isFullForm;
      this.isCompany = $event;
      this.$emit("basicDataChanged", { full_invoice: $event });

      if (this.showShippingDetails) {
        this.withShippingAddress = !this.fullForm;

        this.$emit("basicDataChanged", {
          with_shipping_address: this.withShippingAddress,
        });
      }
    },

    onShowShippingDetailsChanged(): void {
      this.withShippingAddress = this.forceShipping;

      if (!this.forceShipping) {
        this.withShippingAddress = false;
      }

      this.$emit("basicDataChanged", {
        with_shipping_address: this.withShippingAddress,
      });
    },
  },

  watch: {
    showShippingDetails(): void {
      this.onShowShippingDetailsChanged();
    },

    forceShipping(value): void {
      this.withShippingAddress = value;
      this.$emit("basicDataChanged", {
        with_shipping_address: this.withShippingAddress,
      });
    },
  },

  mounted(): void {
    this.$emit("updateBasicData", {
      full_invoice: this.isFullForm,
    });

    this.$emit("isAddressShowChanged", this.isAddressShow);

    this.onShowShippingDetailsChanged();
  },
});
</script>
